import { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
	setDistrictFilter as setGlobalDistrictFilter,
	setSchoolFilter as setGlobalSchoolFilter,
	setTeacherFilter as setGlobalTeacherFilter,
	setCourseFilter as setGlobalCourseFilter,
	setActiveSubTab as setGlobalActiveSubTab,
} from "../../../app/adminReportsFilterSlice";
import { getMaxReportLevel } from "./adminReportsUtil";
import { ReportLevel } from "./adminReportsApiSlice";
import { AdminReportsSubTabs } from "./AdminReports";
import { useAdminReportsUrlParams } from "./useAdminReportsUrlParams";
import { useReportLevelDistrictAndSchoolOptions } from "./useReportLevelDistrictAndSchoolOptions";
import { useTopLevelOrgSync } from "./useTopLevelOrgSync";
import { useTabChangeReset } from "./useTabChangeReset";
import { useTeacherAndCourseOptions } from "./useTeacherAndCourseOptions";

// This hook is made to allow the filters to interact and remain constant along the entire reports stream
// across all admin report pages, so that a change made on one, changes the filters on another page.
//
// It composes several smaller, single-purpose hooks:
// - useAdminReportsUrlParams: reads/clears districtId & schoolId URL params
// - useReportLevelDistrictAndSchoolOptions: fetches district/school options for a given
//   report level (called once for the Admin tab, once for the Course tab)
// - useTopLevelOrgSync: syncs filters when the user changes org context via the header picker
// - useTabChangeReset: resets filters when switching tabs if the selection is no longer valid
// - useTeacherAndCourseOptions: cascading teacher -> course option fetching for the Course tab
export function useAdminReportsFilters() {
	const dispatch = useAppDispatch();
	const { role, school, district } = useAppSelector((state) => state.user);
	const districtFilter = useAppSelector((state) => state.adminReportsFilter.districtFilter);
	const schoolFilter = useAppSelector((state) => state.adminReportsFilter.schoolFilter);
	const teacherFilter = useAppSelector((state) => state.adminReportsFilter.teacherFilter);
	const courseFilter = useAppSelector((state) => state.adminReportsFilter.courseFilter);
	const activeSubTab = useAppSelector((state) => state.adminReportsFilter.activeSubTab);

	const maxReportLevel = getMaxReportLevel(role);

	const { districtIdParam, schoolIdParam } = useAdminReportsUrlParams();

	const setDistrictFilter = (value: number) => {
		dispatch(setGlobalDistrictFilter(value));
	};

	const setSchoolFilter = (value: number) => {
		dispatch(setGlobalSchoolFilter(value));
	};

	const setTeacherFilter = (value: number) => {
		dispatch(setGlobalTeacherFilter(value));
	};

	const setCourseFilter = (value: number) => {
		dispatch(setGlobalCourseFilter(value));
	};

	const setActiveSubTab = (value: string) => {
		dispatch(setGlobalActiveSubTab(value));
	};

	// Only set the active subtab if it hasn't been set yet
	useEffect(() => {
		if (activeSubTab === null) {
			setActiveSubTab(AdminReportsSubTabs.Admin);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeSubTab]);

	const sharedOptionsArgs = {
		maxReportLevel,
		topLevelDistrictId: district?.id,
		topLevelSchoolId: school?.id,
		districtFilter,
		schoolFilter,
		districtIdParam,
		schoolIdParam,
		role,
		userSchool: school,
		userDistrict: district,
		setDistrictFilter,
		setSchoolFilter,
	};

	const { districtOptions: adminLevelDistrictOptions, schoolOptions: adminLevelSchoolOptions } =
		useReportLevelDistrictAndSchoolOptions({
			...sharedOptionsArgs,
			level: maxReportLevel,
			isActive: activeSubTab === AdminReportsSubTabs.Admin,
		});

	const { districtOptions: courseLevelDistrictOptions, schoolOptions: courseLevelSchoolOptions } =
		useReportLevelDistrictAndSchoolOptions({
			...sharedOptionsArgs,
			level: ReportLevel.Course,
			isActive: activeSubTab === AdminReportsSubTabs.Course,
		});

	// Expose the option set that corresponds to the currently active tab
	const districtOptions =
		activeSubTab === AdminReportsSubTabs.Course
			? courseLevelDistrictOptions
			: adminLevelDistrictOptions;
	const schoolOptions =
		activeSubTab === AdminReportsSubTabs.Course
			? courseLevelSchoolOptions
			: adminLevelSchoolOptions;

	useTabChangeReset({
		activeSubTab,
		maxReportLevel,
		adminLevelDistrictOptions,
		adminLevelSchoolOptions,
		courseLevelDistrictOptions,
		courseLevelSchoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	});

	useTopLevelOrgSync({
		role,
		topLevelDistrictId: district?.id,
		topLevelSchoolId: school?.id,
		setDistrictFilter,
		setSchoolFilter,
	});

	const { teacherOptions, courseOptions } = useTeacherAndCourseOptions({
		schoolFilter,
		teacherFilter,
		courseFilter,
		setTeacherFilter,
		setCourseFilter,
	});

	return {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		teacherOptions,
		courseOptions,
		districtFilter,
		schoolFilter,
		teacherFilter,
		courseFilter,
		activeSubTab,
		setDistrictFilter,
		setSchoolFilter,
		setTeacherFilter,
		setCourseFilter,
		setActiveSubTab,
	};
}
